import React, {FC, useEffect, useRef, useState} from "react";
import NcModal from "components/NcModal/NcModal";
import Textarea from "components/Textarea/Textarea";
import ButtonPrimary from "components/Button/ButtonPrimary";
import ButtonSecondary from "components/Button/ButtonSecondary";
import {RadioGroup} from "@headlessui/react";
import twFocusClass from "utils/twFocusClass";
import {collection, doc, serverTimestamp, setDoc} from "firebase/firestore";
import {db} from "../../configs/firebase-config";
import {getTimeStamp} from "../../global/Functions";

export interface ProblemPlan {
    name: string;
    id: string;
    label: string;
}

export interface ModalReportItemProps {
    id: number | string;
    postId? : string;
    show: boolean;
    problemPlans?: ProblemPlan[];
    onCloseModalReportItem: () => void;
}

const problemPlansDemo = [
    {name: "Scam", id: "Scam", label: "Scam"},
    {name: "Sexual", id: "Sexual", label: "Sexual"},
    {name: "Offensive", id: "Offensive", label: "Offensive"},
    {name: "Violence", id: "Violence", label: "Violence"},
    {name: "Fraud", id: "Fraud", label: "Fraud"},
    {name: "Spam", id: "Spam", label: "Spam"},
    {name: "FakeNews", id: "FakeNews", label: "Fake News"},
    {name: "Other", id: "Other", label: "Other"},
];

const ModalReportItem: FC<ModalReportItemProps> = ({
                                                       problemPlans = problemPlansDemo,
                                                       id,
                                                       postId="",
                                                       show,
                                                       onCloseModalReportItem,
                                                   }) => {
  const textareaRef = useRef(null);
  const [problemSelected, setProblemSelected] = useState(problemPlans[0]);
  const [reportReason, setReportReason] = useState("");

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        const element: HTMLTextAreaElement | null = textareaRef.current;
        if (element) {
            (element as HTMLTextAreaElement).focus();
        }
      }, 400);
    }
  }, [show]);

  const handleClickSubmitForm = async (e: any) => {
    e.preventDefault();
    const abuseCollectionRef = collection(db,"report");
    await setDoc(doc(abuseCollectionRef, `${id}_${getTimeStamp()}`), {
      id,
      postId,
      timestamp: serverTimestamp(),
      problem: problemSelected,
      message: reportReason,
    })
    onCloseModalReportItem();
    console.log({
      id,
      problem: problemSelected,
      message: (textareaRef.current as unknown as HTMLTextAreaElement).value,
    });
  };

  const renderCheckIcon = () => {
    return (
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
        <circle cx={12} cy={12} r={12} fill="#fff" opacity="0.2"/>
        <path
          d="M7 13l3 3 7-7"
          stroke="#fff"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const renderContent = () => {
    return (
      <form onSubmit={handleClickSubmitForm}>
        {/* RADIO PROBLEM PLANS */}
        <RadioGroup value={problemSelected} onChange={setProblemSelected}>
          <RadioGroup.Label className="sr-only">Problem Plans</RadioGroup.Label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {problemPlans.map((plan) => (
              <RadioGroup.Option
                key={plan.name}
                value={plan}
                className={({checked}) => {
                  return (
                    `${checked
                      ? "bg-primary-6000 text-white dark:bg-primary-700"
                      : "bg-white border-t border-neutral-50 "
                    } relative shadow-lg rounded-lg px-3 py-3 cursor-pointer flex sm:px-5 sm:py-4 focus:outline-none ` + twFocusClass(true)
                  );
                }}
              >
                {({checked}) => (
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <RadioGroup.Label
                          as="p"
                          className={`font-medium line-clamp-1 ${
                              checked ? "text-white" : "text-neutral-900" 
                          }`}
                        >
                          {plan.label}
                        </RadioGroup.Label>
                      </div>
                  </div>
                  {checked && (
                    <div className="flex-shrink-0 text-white">
                      {renderCheckIcon()}
                    </div>
                    )}
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        <div className="mt-4">
          <h4 className="text-lg font-semibold text-neutral-700 dark:text-neutral-200">
            Reason
          </h4>
          <span className="text-sm text-neutral-6000 dark:text-neutral-400">
            Please provide any additional information or context that will help
            us understand and effectively handle the situation.
          </span>
          <Textarea
            placeholder="Please provide as much detail as possible..."
            className="mt-3"
            ref={textareaRef}
            required={true}
            rows={4}
            id="report-message"
            onChange={(e) => setReportReason(e.target.value)}
          />
        </div>
          <div className="mt-4 space-x-3">
            <ButtonPrimary type="submit">
              Report
            </ButtonPrimary>
            <ButtonSecondary type="reset" onClick={onCloseModalReportItem}>
              Cancel
            </ButtonSecondary>
          </div>
      </form>
    );
  };

  const renderTrigger = () => {
    return null;
  };

  return (
    <NcModal
      isOpenProp={show}
      onCloseModal={onCloseModalReportItem}
      contentExtraClass="max-w-screen-md"
      renderContent={renderContent}
      renderTrigger={renderTrigger}
      modalTitle="Report Content"
    />
  );
};

export default ModalReportItem;
